import { ChainIds, type MultichainTokenBalance } from "@umami/chains";
import { useDynamicModalContext } from "@umami/components";
import { type UnifiedBalanceWithPrices, useCurrentAccount, useGetNetworkChain } from "@umami/state";
import {
  BridgeDirection,
  type GaslessQuoteResponse,
  type LiFiQuoteResponse,
  type ZeroXQuoteResponse,
} from "@umami/swap";
import { CustomError } from "@umami/utils";
import { useNavigate } from "react-router-dom";

import { AssetSelectModal } from "@/components/Swap/AssetSelectModal";
import { ConfirmSwapModal } from "@/components/Swap/ConfirmSwapModal";
import { FormPage as SwapTokenFormPage } from "@/components/Swap/FormPage";
import { OperationSubmittedModal } from "@/components/Swap/OperationSubmittedModal";
import { type QuoteWithProvider } from "@/components/Swap/providers";

export const useSwapTokenFlow = () => {
  const { openWith } = useDynamicModalContext();
  const currentAccount = useCurrentAccount()!;
  const navigate = useNavigate();
  const getNetworkChain = useGetNetworkChain();

  const handleSelectSell = async ({ buyToken }: { buyToken: UnifiedBalanceWithPrices }) => {
    await openWith(<AssetSelectModal buyToken={buyToken} mode="sell" />, { canBeOverridden: true });
  };

  const handleSelectBuy = async ({ sellToken }: { sellToken: UnifiedBalanceWithPrices }) => {
    await openWith(<AssetSelectModal mode="buy" sellToken={sellToken} />, {
      canBeOverridden: true,
    });
  };

  const handleConfirmSwap = async (params: {
    sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
    sellAmount: string;
    buyAmount: string;
    buyFiatAmount: string;
    fiatValue: string;
    sellFiatAmount: string;
    slippage: number;
    zeroXQuote?: ZeroXQuoteResponse | null;
    gaslessQuote?: GaslessQuoteResponse | null;
    lifiQuote?: LiFiQuoteResponse | null;
    selectedQuote?: QuoteWithProvider | null;
    isGaslessSwap?: boolean;
    isFastWithdrawal?: boolean;
  }) => {
    const {
      sellToken,
      buyToken,
      slippage,
      buyFiatAmount,
      buyAmount,
      sellAmount,
      sellFiatAmount,
      fiatValue,
      zeroXQuote,
      gaslessQuote,
      lifiQuote,
      selectedQuote,
      isGaslessSwap,
      isFastWithdrawal,
    } = params;

    // TODO: implement when we will add Swaps
    const minimumReceived = "";

    // Determine bridge direction based on token chains
    let direction: BridgeDirection;
    switch (getNetworkChain(sellToken.chainId)) {
      case ChainIds.TEZOS:
        direction = BridgeDirection.TezosToEtherlink;
        break;
      case ChainIds.ETHERLINK:
        switch (getNetworkChain(buyToken.chainId)) {
          case ChainIds.TEZOS:
            direction = BridgeDirection.EtherlinkToTezos;
            break;
          case ChainIds.ETHEREUM:
            direction = BridgeDirection.EtherlinkToEthereum;
            break;
          case ChainIds.BSC:
            direction = BridgeDirection.EtherlinkToBsc;
            break;
          case ChainIds.ETHERLINK:
            direction = BridgeDirection.EtherlinkToBsc;
            break;
          case ChainIds.BITCOIN:
            throw new CustomError("Bitcoin swap is not supported yet");
        }
        break;
      case ChainIds.ETHEREUM:
        direction =
          getNetworkChain(buyToken.chainId) === ChainIds.ETHERLINK
            ? BridgeDirection.EthereumToEtherlink
            : BridgeDirection.EthereumToBsc;
        break;
      case ChainIds.BSC:
        direction =
          getNetworkChain(buyToken.chainId) === ChainIds.ETHEREUM
            ? BridgeDirection.BscToEthereum
            : BridgeDirection.BscToEtherlink;
        break;
      case ChainIds.BITCOIN:
        throw new CustomError("Bitcoin swap is not supported yet");
    }

    await openWith(
      <ConfirmSwapModal
        direction={direction}
        buyAmount={buyAmount}
        buyFiatAmount={buyFiatAmount}
        buyToken={buyToken}
        conversionRate={1.23}
        fiatValue={fiatValue}
        gaslessQuote={gaslessQuote}
        isFastWithdrawal={isFastWithdrawal}
        isGaslessSwap={isGaslessSwap}
        lifiQuote={lifiQuote}
        minimumReceived={minimumReceived}
        onConfirm={handleOperationSubmitted}
        priceImpact={-0.05}
        selectedQuote={selectedQuote}
        sellAmount={sellAmount}
        sellFiatAmount={sellFiatAmount}
        sellToken={sellToken}
        slippage={slippage}
        zeroXQuote={zeroXQuote}
      />
    );
  };

  const handleOperationSubmitted = async () => {
    await openWith(
      <OperationSubmittedModal
        onSeeAllOperations={() => {
          navigate("/activity");
        }}
      />
    );
  };

  const handleSwapClick = async () => {
    await openWith(
      <SwapTokenFormPage
        onPreviewClick={params => handleConfirmSwap(params)}
        onSelectBuyTokenClick={handleSelectBuy}
        onSelectSellTokenClick={handleSelectSell}
        sender={currentAccount}
      />
    );
  };

  return {
    handleSwapClick,
  };
};
